import { styled } from '@mui/material/styles';
import Button, { ButtonProps } from '@mui/material/Button';
import { DESIGN_TOKENS } from '../DesignSystem/DesignSystem';

// ============================================================================
// BUTTON COMPONENTS - Following DRY principle
// ============================================================================

/**
 * Primary button component with consistent styling
 * Uses design system tokens for maintainability
 */
export const MainButton = styled(Button)<ButtonProps>(({ theme }) => ({
    backgroundColor: DESIGN_TOKENS.colors.primary,
    color: DESIGN_TOKENS.colors.secondary,
    borderRadius: DESIGN_TOKENS.borderRadius.pill,
    fontFamily: DESIGN_TOKENS.typography.fontFamily.primary,
    textTransform: 'none',
    padding: `${theme.spacing(DESIGN_TOKENS.spacing.sm)} ${theme.spacing(DESIGN_TOKENS.spacing.xl)}`,
    transition: DESIGN_TOKENS.transitions.normal,
    minHeight: '48px', // Touch-friendly height
    fontWeight: 600,

    '&:hover, &:focus, &:active': {
        backgroundColor: DESIGN_TOKENS.colors.primary,
        color: DESIGN_TOKENS.colors.secondary,
        opacity: 0.8,
        transform: 'translateY(-2px)',
        boxShadow: DESIGN_TOKENS.shadows.medium,
    },

    [theme.breakpoints.up('sm')]: {
        fontSize: DESIGN_TOKENS.typography.fontSize.md,
        padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
    },

    [theme.breakpoints.up('md')]: {
        fontSize: DESIGN_TOKENS.typography.fontSize.md,
        padding: `${theme.spacing(2)} ${theme.spacing(5)}`,
    },

    [theme.breakpoints.up('lg')]: {
        fontSize: DESIGN_TOKENS.typography.fontSize.md,
        padding: `${theme.spacing(2)} ${theme.spacing(6)}`,
    },

    // Mobile-specific optimizations
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        fontSize: '16px', // Prevent zoom on iOS
        minHeight: '52px',
    },

    // Disable hover effects on touch devices
    '@media (hover: none)': {
        '&:hover': {
            transform: 'none',
            boxShadow: 'none',
        },
    },
}));

/**
 * Secondary button component with consistent styling
 * Uses design system tokens for maintainability
 */
export const SecondaryButton = styled(Button)<ButtonProps>(({ theme }) => ({
    backgroundColor: DESIGN_TOKENS.colors.secondary,
    color: DESIGN_TOKENS.colors.text.primary,
    borderRadius: DESIGN_TOKENS.borderRadius.pill,
    fontFamily: DESIGN_TOKENS.typography.fontFamily.primary,
    textTransform: 'none',
    padding: `${theme.spacing(DESIGN_TOKENS.spacing.sm)} ${theme.spacing(DESIGN_TOKENS.spacing.xl)}`,
    transition: DESIGN_TOKENS.transitions.normal,
    minHeight: '48px', // Touch-friendly height
    fontWeight: 600,
    border: `2px solid ${DESIGN_TOKENS.colors.primary}`,

    '&:hover, &:focus, &:active': {
        backgroundColor: DESIGN_TOKENS.colors.primary,
        color: DESIGN_TOKENS.colors.secondary,
        transform: 'translateY(-2px)',
        boxShadow: DESIGN_TOKENS.shadows.medium,
    },

    [theme.breakpoints.up('sm')]: {
        fontSize: DESIGN_TOKENS.typography.fontSize.md,
        padding: `${theme.spacing(2)} ${theme.spacing(4)}`,
    },

    [theme.breakpoints.up('md')]: {
        fontSize: DESIGN_TOKENS.typography.fontSize.md,
        padding: `${theme.spacing(2)} ${theme.spacing(5)}`,
    },

    [theme.breakpoints.up('lg')]: {
        fontSize: DESIGN_TOKENS.typography.fontSize.md,
        padding: `${theme.spacing(2)} ${theme.spacing(6)}`,
    },

    // Mobile-specific optimizations
    [theme.breakpoints.down('sm')]: {
        width: '100%',
        fontSize: '16px', // Prevent zoom on iOS
        minHeight: '52px',
    },

    // Disable hover effects on touch devices
    '@media (hover: none)': {
        '&:hover': {
            transform: 'none',
            boxShadow: 'none',
        },
    },
}));

// ============================================================================
// EXPORT ALL BUTTON COMPONENTS
// ============================================================================

export default {
    MainButton,
    SecondaryButton,
};
